import { Extension, Mark, mergeAttributes } from '@tiptap/core';
import { DOMParser } from '@tiptap/pm/model';
import { diffWords } from 'diff';

// Define the insertion mark
export const InsertionMark = Mark.create({
  name: 'insertion',

  addAttributes() {
    return {};
  },

  parseHTML() {
    return [{ tag: 'insert' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['insert', mergeAttributes(HTMLAttributes), 0];
  },
});

// Define the deletion mark
export const DeletionMark = Mark.create({
  name: 'deletion',

  addAttributes() {
    return {};
  },

  parseHTML() {
    return [{ tag: 'delete' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['delete', mergeAttributes(HTMLAttributes), 0];
  },
});

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    documentDiff: {
      showDocumentDiff: (options: { originalContent: string; modifiedContent: string }) => ReturnType;
      showDocumentDiffFromJSON: (options: { originalDoc: any; modifiedDoc: any }) => ReturnType;
      clearDocumentDiff: () => ReturnType;
      acceptAllChanges: () => ReturnType;
      rejectAllChanges: () => ReturnType;
    };
  }
}

export const DocumentDiff = Extension.create({
  name: 'documentDiff',

  addExtensions() {
    return [InsertionMark, DeletionMark];
  },

  addCommands() {
    return {
      showDocumentDiff:
        ({ originalContent, modifiedContent }) =>
        ({ editor, tr, state }) => {
          // Use ProseMirror's DOMParser to properly parse HTML into document structure
          const parser = DOMParser.fromSchema(editor.schema);

          // Parse original content
          const originalDiv = document.createElement('div');
          originalDiv.innerHTML = originalContent;
          const originalDoc = parser.parse(originalDiv).toJSON();

          // Parse modified content
          const modifiedDiv = document.createElement('div');
          modifiedDiv.innerHTML = modifiedContent;
          const modifiedDoc = parser.parse(modifiedDiv).toJSON();

          // Create a merged document with diff marks
          const mergedDoc = mergeDocs(originalDoc, modifiedDoc, editor.schema);

          try {
            // Create the new document node
            const newDoc = editor.schema.nodeFromJSON(mergedDoc);

            // Replace the entire document content
            tr.replaceWith(0, state.doc.content.size, newDoc.content);

            return true;
          } catch (error) {
            console.error('Failed to create document from JSON:', error);
            return false;
          }
        },

      showDocumentDiffFromJSON:
        ({ originalDoc, modifiedDoc }) =>
        ({ editor, tr, state }) => {
          // Create a merged document with diff marks directly from JSON
          const mergedDoc = mergeDocs(originalDoc, modifiedDoc, editor.schema);

          try {
            // Create the new document node
            const newDoc = editor.schema.nodeFromJSON(mergedDoc);

            // Replace the entire document content
            tr.replaceWith(0, state.doc.content.size, newDoc.content);

            return true;
          } catch (error) {
            console.error('Failed to create document from JSON:', error);
            return false;
          }
        },

      clearDocumentDiff:
        () =>
        ({ commands }) => {
          // Remove all diff marks
          commands.unsetMark('insertion');
          commands.unsetMark('deletion');
          return true;
        },

      acceptAllChanges:
        () =>
        ({ tr, state }) => {
          // Remove deletion marks and the content they mark
          const mapping = tr.mapping;

          // Collect ranges and blocks to delete
          const rangesToDelete: Array<{ from: number; to: number }> = [];
          const blocksToDelete: Array<{ from: number; to: number }> = [];
          const processedBlocks = new Set<number>();

          state.doc.descendants((node, pos) => {
            if (node.isText && node.marks.some((mark) => mark.type.name === 'deletion')) {
              // Check if this is our placeholder space for empty paragraphs
              if (node.text === ' ' && node.nodeSize === 1) {
                // Find the containing draggableBlock
                const $pos = state.doc.resolve(pos);
                let depth = $pos.depth;

                // Walk up to find draggableBlock
                while (depth > 0) {
                  const nodeAtDepth = $pos.node(depth);
                  if (nodeAtDepth.type.name === 'draggableBlock') {
                    const blockStart = $pos.before(depth);
                    const blockEnd = blockStart + nodeAtDepth.nodeSize;

                    // Only add if we haven't processed this block yet
                    if (!processedBlocks.has(blockStart)) {
                      blocksToDelete.push({ from: blockStart, to: blockEnd });
                      processedBlocks.add(blockStart);
                    }
                    break;
                  }
                  depth--;
                }

                if (depth === 0) {
                  rangesToDelete.push({ from: pos, to: pos + node.nodeSize });
                }
              } else {
                // Regular text with deletion mark
                rangesToDelete.push({ from: pos, to: pos + node.nodeSize });
              }
            }
          });

          // Delete entire blocks first (in reverse order)
          blocksToDelete
            .sort((a, b) => b.from - a.from)
            .forEach(({ from, to }) => {
              tr.delete(mapping.map(from), mapping.map(to));
            });

          // Then delete text ranges (in reverse order)
          rangesToDelete
            .sort((a, b) => b.from - a.from)
            .forEach(({ from, to }) => {
              tr.delete(mapping.map(from), mapping.map(to));
            });

          // Remove insertion marks (keep the content)
          tr.removeMark(0, tr.doc.content.size, state.schema.marks.insertion);

          return true;
        },

      rejectAllChanges:
        () =>
        ({ tr, state }) => {
          // For reject all:
          // 1. Remove entire nodes that contain only insertions
          // 2. Remove insertion marks from mixed content
          // 3. Remove deletion marks (keep the content)

          const nodesToDelete: Array<{ from: number; to: number }> = [];
          const textRangesToDelete: Array<{ from: number; to: number }> = [];

          // First pass: identify nodes that are entirely inserted
          state.doc.descendants((node, pos) => {
            if (node.type.name === 'draggableBlock') {
              // Check if this draggableBlock contains only inserted content
              let hasOnlyInsertions = true;
              let hasAnyContent = false;

              node.descendants((child, childPos) => {
                if (child.isText) {
                  const hasDeletion = child.marks.some((mark) => mark.type.name === 'deletion');
                  const hasInsertion = child.marks.some((mark) => mark.type.name === 'insertion');

                  // Skip placeholder spaces (both with deletion marks and insertion marks)
                  if (child.textContent === ' ' && (hasDeletion || hasInsertion)) {
                    return; // Skip this placeholder space
                  }

                  // Only count as real content if it has meaningful text
                  if (child.textContent.trim() !== '') {
                    hasAnyContent = true;

                    // If it has content that's not inserted, this block has mixed content
                    if (!hasInsertion || hasDeletion) {
                      hasOnlyInsertions = false;
                    }
                  }
                }
              });

              // Check if this block contains only placeholder spaces
              let hasOnlyPlaceholderSpaces = false;
              if (!hasAnyContent) {
                // No real content, check if it has placeholder spaces
                node.descendants((child, childPos) => {
                  if (child.isText && child.textContent === ' ') {
                    const hasDiffMarks = child.marks.some(
                      (mark) => mark.type.name === 'insertion' || mark.type.name === 'deletion',
                    );
                    if (hasDiffMarks) {
                      hasOnlyPlaceholderSpaces = true;
                    }
                  }
                });
              }

              // If the block has content and it's all inserted, OR if it only has placeholder spaces, mark for deletion
              if ((hasAnyContent && hasOnlyInsertions) || hasOnlyPlaceholderSpaces) {
                nodesToDelete.push({ from: pos, to: pos + node.nodeSize });
                return false; // Don't descend into this node
              }
            } else if (node.type.name === 'paragraph') {
              // Check if this paragraph contains only inserted content (for cases without draggableBlock)
              let hasOnlyInsertions = true;
              let hasAnyContent = false;

              node.descendants((child, childPos) => {
                if (child.isText) {
                  const hasDeletion = child.marks.some((mark) => mark.type.name === 'deletion');
                  const hasInsertion = child.marks.some((mark) => mark.type.name === 'insertion');

                  // Skip placeholder spaces (both with deletion marks and insertion marks)
                  if (child.textContent === ' ' && (hasDeletion || hasInsertion)) {
                    return; // Skip this placeholder space
                  }

                  // Only count as real content if it has meaningful text
                  if (child.textContent.trim() !== '') {
                    hasAnyContent = true;

                    // If it has content that's not inserted, this paragraph has mixed content
                    if (!hasInsertion || hasDeletion) {
                      hasOnlyInsertions = false;
                    }
                  }
                }
              });

              // Check if this paragraph contains only placeholder spaces
              let hasOnlyPlaceholderSpaces = false;
              if (!hasAnyContent) {
                // No real content, check if it has placeholder spaces
                node.descendants((child, childPos) => {
                  if (child.isText && child.textContent === ' ') {
                    const hasDiffMarks = child.marks.some(
                      (mark) => mark.type.name === 'insertion' || mark.type.name === 'deletion',
                    );
                    if (hasDiffMarks) {
                      hasOnlyPlaceholderSpaces = true;
                    }
                  }
                });
              }

              // If the paragraph has content and it's all inserted, OR if it only has placeholder spaces, mark for deletion
              if ((hasAnyContent && hasOnlyInsertions) || hasOnlyPlaceholderSpaces) {
                nodesToDelete.push({ from: pos, to: pos + node.nodeSize });
                return false; // Don't descend into this node
              }
            }

            // Handle individual text nodes with insertion marks (for mixed content)
            if (node.isText && node.marks.some((mark) => mark.type.name === 'insertion')) {
              textRangesToDelete.push({ from: pos, to: pos + node.nodeSize });
            }
          });

          // Delete entire nodes first (in reverse order)
          nodesToDelete.reverse().forEach(({ from, to }) => {
            tr.delete(from, to);
          });

          // Then delete individual text ranges (in reverse order, using mapping)
          textRangesToDelete.reverse().forEach(({ from, to }) => {
            const mappedFrom = tr.mapping.map(from);
            const mappedTo = tr.mapping.map(to);
            if (mappedFrom < tr.doc.content.size && mappedTo <= tr.doc.content.size) {
              tr.delete(mappedFrom, mappedTo);
            }
          });

          // Remove deletion marks (keep the content)
          tr.removeMark(0, tr.doc.content.size, state.schema.marks.deletion);

          return true;
        },
    };
  },
});

// Function to merge two documents with diff marks
function mergeDocs(originalDoc: any, modifiedDoc: any, schema: any): any {
  const originalBlocks = originalDoc.content || [];
  const modifiedBlocks = modifiedDoc.content || [];

  const mergedContent: any[] = [];

  // Use content-based matching instead of position-based matching
  const usedOriginalIndices = new Set<number>();
  const usedModifiedIndices = new Set<number>();

  // First pass: find exact matches regardless of position
  for (let modIdx = 0; modIdx < modifiedBlocks.length; modIdx++) {
    if (usedModifiedIndices.has(modIdx)) continue;

    const modifiedBlock = modifiedBlocks[modIdx];
    let bestMatch = -1;
    let bestSimilarity = 0;

    // Find the best matching original block
    for (let origIdx = 0; origIdx < originalBlocks.length; origIdx++) {
      if (usedOriginalIndices.has(origIdx)) continue;

      const originalBlock = originalBlocks[origIdx];
      const similarity = calculateBlockSimilarity(originalBlock, modifiedBlock);

      if (similarity > bestSimilarity) {
        bestSimilarity = similarity;
        bestMatch = origIdx;
      }
    }

    if (bestMatch >= 0 && bestSimilarity > 0.3) {
      // Found a good match - merge the blocks
      const originalBlock = originalBlocks[bestMatch];
      usedOriginalIndices.add(bestMatch);
      usedModifiedIndices.add(modIdx);

      if (bestSimilarity === 1) {
        // Identical blocks - no diff needed
        mergedContent.push({ ...modifiedBlock, _origIdx: bestMatch, _modIdx: modIdx });
      } else {
        // Similar blocks - merge them
        const mergedBlock = mergeBlocks(originalBlock, modifiedBlock);
        mergedContent.push({ ...mergedBlock, _origIdx: bestMatch, _modIdx: modIdx });
      }
    }
  }

  // Second pass: handle unmatched blocks
  // Add deleted blocks (original blocks that weren't matched)
  for (let origIdx = 0; origIdx < originalBlocks.length; origIdx++) {
    if (!usedOriginalIndices.has(origIdx)) {
      const deletedBlock = markBlockAsDeleted(originalBlocks[origIdx]);
      mergedContent.push({ ...deletedBlock, _origIdx: origIdx, _modIdx: -1 });
    }
  }

  // Add inserted blocks (modified blocks that weren't matched)
  for (let modIdx = 0; modIdx < modifiedBlocks.length; modIdx++) {
    if (!usedModifiedIndices.has(modIdx)) {
      const insertedBlock = markBlockAsInserted(modifiedBlocks[modIdx]);
      mergedContent.push({ ...insertedBlock, _origIdx: -1, _modIdx: modIdx });
    }
  }

  // Sort by modified position to maintain document order
  mergedContent.sort((a, b) => {
    // Prioritize modified document order for insertions, but place deletions appropriately
    if (a._modIdx >= 0 && b._modIdx >= 0) {
      return a._modIdx - b._modIdx;
    } else if (a._modIdx >= 0 && b._modIdx < 0) {
      // a is insertion/match, b is deletion - insertion comes first if it's at similar position
      return a._modIdx - b._origIdx;
    } else if (a._modIdx < 0 && b._modIdx >= 0) {
      // a is deletion, b is insertion/match
      return a._origIdx - b._modIdx;
    } else {
      // Both are deletions
      return a._origIdx - b._origIdx;
    }
  });

  // Clean up temporary indices
  mergedContent.forEach((block) => {
    delete block._origIdx;
    delete block._modIdx;
  });

  return {
    type: 'doc',
    content: mergedContent,
  };
}

// Calculate similarity between two blocks (0-1)
function calculateBlockSimilarity(block1: any, block2: any): number {
  if (block1.type !== block2.type) return 0;

  // For draggableBlocks, compare their content
  if (block1.type === 'draggableBlock' && block2.type === 'draggableBlock') {
    const content1 = block1.content?.[0];
    const content2 = block2.content?.[0];

    // Handle missing content
    if (!content1 && !content2) return 1; // Both empty - perfect match
    if (!content1 || !content2) return 0; // One empty, one not - no match
    if (content1.type !== content2.type) return 0;

    // Get text content for comparison
    const text1 = getTextFromNode(content1);
    const text2 = getTextFromNode(content2);

    // Calculate similarity based on text content
    if (text1 === text2) return 1; // Exact match

    // Calculate similarity score based on common words
    const words1 = text1
      .toLowerCase()
      .split(/\s+/)
      .filter((w) => w.length > 0);
    const words2 = text2
      .toLowerCase()
      .split(/\s+/)
      .filter((w) => w.length > 0);

    // If both are empty, they match exactly
    if (words1.length === 0 && words2.length === 0) return 1;

    // If one is empty and the other isn't, no similarity
    if (words1.length === 0 || words2.length === 0) return 0;

    const commonWords = words1.filter((w) => words2.includes(w)).length;
    const totalWords = Math.max(words1.length, words2.length);

    return totalWords > 0 ? commonWords / totalWords : 0;
  }

  return 0;
}

function markBlockAsInserted(block: any): any {
  return {
    ...block,
    content: block.content?.map((node: any) => markNodeAsInserted(node)),
  };
}

function markBlockAsDeleted(block: any): any {
  return {
    ...block,
    content: block.content?.map((node: any) => markNodeAsDeleted(node)),
  };
}

function markNodeAsInserted(node: any): any {
  if (node.type === 'text') {
    return {
      ...node,
      marks: [
        ...(node.marks || []),
        {
          type: 'insertion',
        },
      ],
    };
  }

  return {
    ...node,
    content: node.content?.map((child: any) => markNodeAsInserted(child)),
  };
}

function markNodeAsDeleted(node: any): any {
  if (node.type === 'text') {
    return {
      ...node,
      marks: [
        ...(node.marks || []),
        {
          type: 'deletion',
        },
      ],
    };
  }

  // For empty paragraphs, add a single space so the deletion mark can be applied
  if (node.type === 'paragraph' && (!node.content || node.content.length === 0)) {
    return {
      ...node,
      content: [
        {
          type: 'text',
          text: ' ',
          marks: [
            {
              type: 'deletion',
            },
          ],
        },
      ],
    };
  }

  return {
    ...node,
    content: node.content?.map((child: any) => markNodeAsDeleted(child)),
  };
}

function mergeBlocks(originalBlock: any, modifiedBlock: any): any {
  // If blocks have the same structure, preserve it and only diff text content
  if (JSON.stringify(originalBlock) === JSON.stringify(modifiedBlock)) {
    return modifiedBlock;
  }

  // We're dealing with draggableBlocks - need to merge their content
  if (originalBlock.type === 'draggableBlock' && modifiedBlock.type === 'draggableBlock') {
    // Get the actual content inside the draggableBlock
    const originalContent = originalBlock.content?.[0];
    const modifiedContent = modifiedBlock.content?.[0];

    if (!originalContent || !modifiedContent) {
      return modifiedBlock;
    }

    // Now handle the actual content type
    if (originalContent.type === modifiedContent.type) {
      let mergedContent;

      if (originalContent.type === 'paragraph') {
        // For paragraphs, do text diff
        mergedContent = mergeTextContent(originalContent, modifiedContent);
      } else if (originalContent.type === 'orderedList' || originalContent.type === 'bulletList') {
        // For lists, merge list items
        mergedContent = mergeListContent(originalContent, modifiedContent);
      } else {
        // For other types, try generic merge
        mergedContent = mergeNodeContent(originalContent, modifiedContent);
      }

      // Return the draggableBlock with merged content
      return {
        ...modifiedBlock,
        content: [mergedContent],
      };
    } else {
      // Different types - show both as changes
      return modifiedBlock;
    }
  }

  // Fallback
  return modifiedBlock;
}

function mergeTextContent(originalNode: any, modifiedNode: any): any {
  // For nodes with complex mark structure, we need to be more careful
  // Get the text nodes with their marks preserved
  const originalTextNodes = extractTextNodes(originalNode);
  const modifiedTextNodes = extractTextNodes(modifiedNode);

  // If the structure is simple (single text node), use word diff
  if (originalTextNodes.length === 1 && modifiedTextNodes.length === 1) {
    const originalText = originalTextNodes[0].text;
    const modifiedText = modifiedTextNodes[0].text;

    if (originalText === modifiedText) {
      return modifiedNode;
    }

    // Simple diff for single text nodes
    const diffs = diffWords(originalText, modifiedText);
    const newContent: any[] = [];

    diffs.forEach((part) => {
      const baseMarks = modifiedTextNodes[0].marks || [];

      if (part.added) {
        newContent.push({
          type: 'text',
          text: part.value,
          marks: [
            ...baseMarks,
            {
              type: 'insertion',
            },
          ],
        });
      } else if (part.removed) {
        newContent.push({
          type: 'text',
          text: part.value,
          marks: [
            ...baseMarks,
            {
              type: 'deletion',
            },
          ],
        });
      } else {
        newContent.push({
          type: 'text',
          text: part.value,
          marks: baseMarks,
        });
      }
    });

    return {
      ...modifiedNode,
      content: newContent,
    };
  }

  // For complex structures with multiple text nodes (e.g., mixed bold/normal text),
  // compare the full structure
  return compareComplexTextNodes(originalNode, modifiedNode, originalTextNodes, modifiedTextNodes);
}

function mergeListContent(originalList: any, modifiedList: any): any {
  // Compare list items
  const originalItems = originalList.content || [];
  const modifiedItems = modifiedList.content || [];

  const mergedItems: any[] = [];
  const maxLength = Math.max(originalItems.length, modifiedItems.length);

  for (let i = 0; i < maxLength; i++) {
    const origItem = originalItems[i];
    const modItem = modifiedItems[i];

    if (!origItem && modItem) {
      // Item added
      mergedItems.push(markNodeAsInserted(modItem));
    } else if (origItem && !modItem) {
      // Item removed
      mergedItems.push(markNodeAsDeleted(origItem));
    } else if (origItem && modItem) {
      // Both exist - merge their content
      mergedItems.push(mergeNodeContent(origItem, modItem));
    }
  }

  return {
    ...modifiedList,
    content: mergedItems,
  };
}

function mergeNodeContent(originalNode: any, modifiedNode: any): any {
  // Generic node content merge
  if (originalNode.type !== modifiedNode.type) {
    // Different types - return modified as-is
    return modifiedNode;
  }

  // If it has text content, do text diff
  if (originalNode.content?.some((child: any) => child.type === 'text')) {
    return mergeTextContent(originalNode, modifiedNode);
  }

  // Otherwise, recursively merge children
  const originalChildren = originalNode.content || [];
  const modifiedChildren = modifiedNode.content || [];

  const mergedChildren: any[] = [];
  const maxLength = Math.max(originalChildren.length, modifiedChildren.length);

  for (let i = 0; i < maxLength; i++) {
    const origChild = originalChildren[i];
    const modChild = modifiedChildren[i];

    if (!origChild && modChild) {
      mergedChildren.push(markNodeAsInserted(modChild));
    } else if (origChild && !modChild) {
      mergedChildren.push(markNodeAsDeleted(origChild));
    } else if (origChild && modChild) {
      mergedChildren.push(mergeNodeContent(origChild, modChild));
    }
  }

  return {
    ...modifiedNode,
    content: mergedChildren,
  };
}

function extractTextNodes(node: any): any[] {
  const textNodes: any[] = [];

  function traverse(n: any) {
    if (!n || !n.content) return;

    n.content.forEach((child: any) => {
      if (child.type === 'text') {
        textNodes.push(child);
      } else {
        traverse(child);
      }
    });
  }

  traverse(node);
  return textNodes;
}

function compareComplexTextNodes(
  originalNode: any,
  modifiedNode: any,
  originalTextNodes: any[],
  modifiedTextNodes: any[],
): any {
  // For complex structures, preserve the modified structure but add diff marks
  // This is a simplified approach - just mark the whole thing as changed if structure differs
  const originalText = originalTextNodes.map((n) => n.text).join('');
  const modifiedText = modifiedTextNodes.map((n) => n.text).join('');

  if (originalText === modifiedText) {
    return modifiedNode;
  }

  // Structure is different - preserve the modified structure and add insertion marks to new content
  const newContent: any[] = [];

  modifiedNode.content.forEach((child: any) => {
    if (child.type === 'text') {
      // Check if this exact text with same marks existed in original
      const existsInOriginal = originalTextNodes.some(
        (orig) => orig.text === child.text && JSON.stringify(orig.marks) === JSON.stringify(child.marks),
      );

      if (!existsInOriginal) {
        // This is new content
        newContent.push({
          ...child,
          marks: [
            ...(child.marks || []),
            {
              type: 'insertion',
            },
          ],
        });
      } else {
        // Keep as is
        newContent.push(child);
      }
    } else {
      newContent.push(child);
    }
  });

  return {
    ...modifiedNode,
    content: newContent,
  };
}

function getTextFromNode(node: any): string {
  if (!node || !node.content) return '';

  return node.content
    .map((child: any) => {
      if (child.type === 'text') {
        return child.text || '';
      }
      return getTextFromNode(child);
    })
    .join('');
}

// Helper function to check if a block is empty
function isBlockEmpty(block: any): boolean {
  if (block.type !== 'draggableBlock') return false;

  const content = block.content?.[0];
  if (!content) return true;

  if (content.type === 'paragraph') {
    const text = getTextFromNode(content);
    return text.trim() === '';
  }

  return false;
}

// Helper function to get clean content without diff marks
export function getCleanContent(editor: any): string | null {
  if (!editor || !editor.storage.markdown) return null;

  const { state } = editor;
  const tr = state.tr;

  // Collect ranges with deletion marks
  const deletionRanges: Array<{ from: number; to: number }> = [];

  state.doc.descendants((node: any, pos: number) => {
    if (node.isText && node.marks.some((mark: any) => mark.type.name === 'deletion')) {
      deletionRanges.push({ from: pos, to: pos + node.nodeSize });
    }
  });

  // Sort ranges in reverse order to delete from end to start
  deletionRanges.sort((a, b) => b.from - a.from);

  // Delete all content marked for deletion
  deletionRanges.forEach(({ from, to }) => {
    tr.delete(from, to);
  });

  // Remove insertion marks from remaining content
  tr.doc.descendants((node: any, pos: number) => {
    if (node.marks.some((mark: any) => mark.type.name === 'insertion')) {
      tr.removeMark(pos, pos + node.nodeSize, state.schema.marks.insertion);
    }
  });

  // Apply transaction to get clean document
  const cleanState = state.apply(tr);
  const originalState = editor.state;

  try {
    // Temporarily update state to get clean markdown
    editor.view.updateState(cleanState);
    const markdown = editor.storage.markdown.getMarkdown();

    // Restore original state
    editor.view.updateState(originalState);

    return markdown;
  } catch (error) {
    console.error('Error getting clean content:', error);
    // Make sure to restore state
    editor.view.updateState(originalState);
    return null;
  }
}

export default DocumentDiff;
